import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
} from '@mui/material'
import { CircleCancelMinor, CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { colors } from 'src/theme'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { TeamRole } from 'src/interfaces/team'
import { groupPermissionsByCategory } from '../utils'
import { removeUnderscoresAndCapitalize } from 'src/utils/baseUtils'
import { GigsafeDefaultUserRoles } from 'src/constants/team/enums'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { useSelector } from 'react-redux'
import { getTeamPermissions } from 'src/selectors/team'

interface Props {
  teamRole: TeamRole
  onRemove(teamRole: TeamRole): void | Promise<void>
}

const ManageableTeamRoleComponent = ({ teamRole, onRemove }: Props) => {
  const teamPermissions = useSelector(getTeamPermissions)

  const { hasPermission } = useCheckPermissions()

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const handleRemove = useCallback(async () => {
    await onRemove(teamRole)
    handleCloseConfirm()
  }, [teamRole, onRemove])

  const toggleAccordion = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [])

  const isDefaultRole = (Object.values(GigsafeDefaultUserRoles) as string[]).includes(teamRole.name)

  const mappedPermissions = useMemo(
    () =>
      teamPermissions ? groupPermissionsByCategory(teamPermissions, teamRole.permissions) : {},
    [teamPermissions, teamRole.permissions],
  )
  const permissionsCount = useMemo(
    () => Object.values(mappedPermissions).reduce((acc, perms) => acc + (perms?.length || 0), 0),
    [mappedPermissions],
  )

  const handleConfirmButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    handleOpenConfirm()
  }, [])

  const handleAccordionButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    toggleAccordion()
  }, [])

  return (
    <Fragment>
      <Accordion
        data-testid={`my-team-manage-roles-item-${teamRole.id}`}
        expanded={expanded}
        disableGutters
        css={tw`shadow-none border-none`}
        sx={{
          '&::before': { display: 'none' },
        }}
      >
        <AccordionSummary
          onClick={toggleAccordion}
          css={tw`flex items-center px-0 border-0 border-b border-solid border-[#EDEDED]`}
          sx={{
            '&::before': { display: 'none' },
          }}
        >
          <div>
            <Typography variant="subtitle1" css={tw`w-[200px]`}>
              {removeUnderscoresAndCapitalize(teamRole.name)}
            </Typography>
            <Typography variant="body2" color={colors.SHADES_GREY_400}>
              {permissionsCount} permissions
            </Typography>
          </div>
          <div css={tw`flex items-center ml-auto gap-2`}>
            {!isDefaultRole && hasPermission(UserPermission.RolesDelete) && (
              <Button
                color="grey"
                size="small"
                className={tw`w-8 h-8`}
                leftIcon={<CircleCancelMinor width={20} />}
                onClick={handleConfirmButtonClick}
                data-testid={`my-team-manage-roles-delete-btn-${teamRole.id}`}
              />
            )}
            <IconButton
              data-testid={`my-team-manage-roles-expand-btn-${teamRole.id}`}
              onClick={handleAccordionButtonClick}
              size="small"
              css={[
                tw`w-8 h-8 flex items-center justify-center border border-solid border-[#EDEDED] rounded-lg`,
                expanded && tw`bg-[#EDEDED]`,
              ]}
            >
              {expanded ? <CaretUpMinor width={20} /> : <CaretDownMinor width={20} />}
            </IconButton>
          </div>
        </AccordionSummary>
        <AccordionDetails
          css={tw`flex flex-col px-3 py-3 gap-2 border-0 border-b border-solid border-[#EDEDED]`}
        >
          {Object.entries(mappedPermissions).map(([category, permissions]) => (
            <div key={category}>
              <Typography variant="body2" css={tw`font-medium leading-5`}>
                {category}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                {(permissions || []).join(', ')}
              </Typography>
            </div>
          ))}
        </AccordionDetails>
      </Accordion>
      <SensitiveActionModal
        isOpen={isConfirmOpen}
        title="Delete role"
        text={`Are you sure you want to delete “${teamRole.name}” role?`}
        submitLabel="Delete"
        onClose={handleCloseConfirm}
        onSubmit={handleRemove}
      />
    </Fragment>
  )
}

export const ManageableTeamRole = memo(ManageableTeamRoleComponent)
